import React from 'react';
import { Button } from '../../../components/button';

/**
 * The Mine | All + Unassigned scope (rendered in the ListFilter scope group) maps to the
 * backend's `mine` / `unassigned` params, which OR together:
 *   All               → everything      Mine               → managed by me
 *   All + Unassigned  → unassigned only Mine + Unassigned  → mine or unassigned
 */
export type AssignmentScope = { mine: boolean; unassigned: boolean };

export const isScoped = ({ mine, unassigned }: AssignmentScope) => mine || unassigned;

/** Noun phrase for the scoped set: "your assignments", "unassigned assignments", ... */
export const scopeNoun = ({ mine, unassigned }: AssignmentScope): string => {
  if (mine && unassigned) return 'your and unassigned assignments';
  if (mine) return 'your assignments';
  if (unassigned) return 'unassigned assignments';
  return 'assignments';
};

const scopedEmptyTitle = (scope: AssignmentScope): string => {
  if (scope.mine && scope.unassigned) return 'None of your or unassigned assignments match';
  if (scope.mine) return 'None of your assignments match';
  return 'No unassigned assignments match';
};

type AssignmentsEmptyStateProps = {
  scope: AssignmentScope;
  /**
   * Count of matches with the scope widened to All (same queue/search/filters); only
   * fetched for an empty scoped list. Undefined while loading or when unscoped.
   */
  allScopeCount?: number;
  onShowAll: () => void;
};

/**
 * Empty list. When a scope (Mine / Unassigned) is narrowing the list, say so and offer a
 * one-click switch to All — users commonly miss that the scope is on.
 */
export const AssignmentsEmptyState: React.FC<AssignmentsEmptyStateProps> = ({
  scope,
  allScopeCount,
  onShowAll,
}) => {
  if (!isScoped(scope)) {
    return (
      <div className="tw:text-center tw:text-zinc-500 tw:py-8" data-testid="assignments-empty">
        No assignments found
        <div className="tw:italic tw:text-zinc-500 tw:text-sm">
          Refine search criteria or check back later
        </div>
      </div>
    );
  }
  const hasMoreInAll = (allScopeCount ?? 0) > 0;
  return (
    <div className="tw:text-center tw:text-zinc-500 tw:py-8" data-testid="assignments-empty">
      {scopedEmptyTitle(scope)}
      <div className="tw:italic tw:text-zinc-500 tw:text-sm">
        {hasMoreInAll
          ? `${allScopeCount} ${allScopeCount === 1 ? 'assignment matches' : 'assignments match'} across all assignments.`
          : 'Refine search criteria or check back later'}
      </div>
      {hasMoreInAll && (
        <div className="tw:mt-3">
          <Button color="brandgreen0" onClick={onShowAll} data-testid="assignments-show-all">
            Show all assignments
          </Button>
        </div>
      )}
    </div>
  );
};
